import React, { useState, useEffect } from 'react';
import { shopService } from '../services/shopService';
import { invitationService } from '../services/invitationService';
import { ShopMembership, MembershipRole, RateLimitError } from '../types';
import { InvitationResponse } from '../types/invitation';
import { useShop } from '../contexts/ShopContext';
import InviteMemberModal from './InviteMemberModal';
import EditMemberRolesModal from './EditMemberRolesModal';
import RateLimitModal from './RateLimitModal';
import { formatDate } from '../utils/dateUtils';

interface ShopMembersTabProps {
  shopId: string;
}

const ShopMembersTab: React.FC<ShopMembersTabProps> = ({ shopId }) => {
  const [members, setMembers] = useState<ShopMembership[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<InvitationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [invitationsLoading, setInvitationsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingMember, setEditingMember] = useState<ShopMembership | null>(null);
  // CSAI-533: activating a member or editing roles can now breach a member limit, so this tab
  // needs the same limit modal the invite flow shows rather than a generic failure string.
  const [rateLimitError, setRateLimitError] = useState<RateLimitError | null>(null);
  const { canManageMembers, userRole, currentMembership } = useShop();

  useEffect(() => {
    loadMembers();
    loadPendingInvitations();
  }, [shopId]);

  const loadMembers = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Loading members for shop:', shopId);
      const shopMembers = await shopService.getShopMemberships(shopId);
      console.log('Loaded shop members:', shopMembers);
      setMembers(shopMembers);
    } catch (error) {
      setError('Failed to load shop members');
      console.error('Error loading members for shop', shopId, ':', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingInvitations = async () => {
    if (!canManageMembers) {
      setInvitationsLoading(false);
      return;
    }

    try {
      setInvitationsLoading(true);
      const invitations = await invitationService.listPendingInvitations(shopId);
      setPendingInvitations(invitations);
    } catch (error) {
      console.error('Error loading pending invitations:', error);
      // Don't set error for invitations - they're supplementary
    } finally {
      setInvitationsLoading(false);
    }
  };

  const handleMemberInvited = (invitation: InvitationResponse) => {
    setPendingInvitations(prev => [...prev, invitation]);
    setShowInviteModal(false);
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    if (!window.confirm('Are you sure you want to revoke this invitation?')) {
      return;
    }

    try {
      await invitationService.revokeInvitation(shopId, invitationId);
      setPendingInvitations(prev => prev.filter(inv => inv.id !== invitationId));
    } catch (error) {
      console.error('Failed to revoke invitation:', error);
      setError('Failed to revoke invitation');
    }
  };

  const handleRoleUpdate = async (membershipId: string, newRoles: MembershipRole[]) => {
    try {
      const updatedMembership = await shopService.updateShopMembership(
        shopId,
        membershipId,
        { roles: newRoles }
      );
      setMembers(prev => prev.map(member =>
        member.id === membershipId ? updatedMembership : member
      ));
    } catch (error) {
      console.error('Failed to update member roles:', error);
      if (error instanceof RateLimitError) {
        setRateLimitError(error);
        return;
      }
      setError('Failed to update member roles');
    }
  };

  const setMemberActive = async (membershipId: string, active: boolean) => {
    try {
      await shopService.updateShopMembership(shopId, membershipId, { active });
      setMembers(prev => prev.map(member =>
        member.id === membershipId ? { ...member, active } : member
      ));
    } catch (error) {
      console.error(`Failed to ${active ? 'activate' : 'deactivate'} member:`, error);
      if (error instanceof RateLimitError) {
        setRateLimitError(error);
        return;
      }
      setError(`Failed to ${active ? 'activate' : 'deactivate'} member`);
    }
  };

  const getRoleDisplayName = (role: MembershipRole): string => {
    const roleNames: Record<MembershipRole, string> = {
      PLATFORM_ADMIN: 'Platform Admin',
      OWNER: 'Owner',
      ADMIN: 'Administrator',
      MEMBER: 'Member',
      VIEWER: 'Viewer',
      CLIENT: 'Client',
      VENDOR: 'Vendor'
    };
    return roleNames[role];
  };

  const getRoleBadgeColor = (role: MembershipRole): string => {
    const roleColors: Record<MembershipRole, string> = {
      PLATFORM_ADMIN: 'bg-purple-100 text-purple-800',
      OWNER: 'bg-gold-100 text-gold-800',
      ADMIN: 'bg-blue-100 text-blue-800',
      MEMBER: 'bg-green-100 text-green-800',
      VIEWER: 'bg-gray-100 text-gray-800',
      CLIENT: 'bg-orange-100 text-orange-800',
      VENDOR: 'bg-teal-100 text-teal-800'
    };
    return roleColors[role] || 'bg-gray-100 text-gray-800';
  };

  const getAvatarColor = (identifier: string | undefined): string => {
    // Default color if identifier is not provided
    if (!identifier) {
      return '#4A6B8A'; // brown
    }

    // Simple hash function to convert string to number
    let hash = 0;
    for (let i = 0; i < identifier.length; i++) {
      hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Predefined palette of pleasant, accessible colors
    const colors = [
      '#4A6B8A', // brown
      '#6B8E23', // olive green
      '#4682B4', // steel blue
      '#CD5C5C', // indian red
      '#9370DB', // medium purple
      '#20B2AA', // light sea green
      '#3D6B9E', // chocolate
      '#4169E1', // royal blue
      '#32CD32', // lime green
      '#FF6347', // tomato
      '#9932CC', // dark orchid
      '#FF8C00', // dark orange
    ];

    // Use hash to select a color from palette
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const getAvatarInitial = (member: ShopMembership): string => {
    if (member.displayName) {
      return member.displayName.charAt(0).toUpperCase();
    }
    if (member.email) {
      return member.email.charAt(0).toUpperCase();
    }
    return member.firebaseUid?.charAt(0)?.toUpperCase() || '?';
  };

  const canEditMember = (member: ShopMembership): boolean => {
    if (!canManageMembers) return false;
    // Users cannot edit their own membership
    if (currentMembership && member.id === currentMembership.id) return false;
    if (member.roles.includes('PLATFORM_ADMIN')) return false;
    if (member.roles.includes('OWNER') && userRole !== 'PLATFORM_ADMIN') return false;
    return true;
  };

  console.log('ShopMembersTab render state:', { loading, error, membersCount: members.length, shopId });

  if (loading) {
    return (
      <div className="members-tab-loading">
        <div className="flex items-center justify-center py-12">
          <div className="spinner-sm mr-3"></div>
          <span className="text-muted">Loading shop members...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-members-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="heading-secondary text-lg">Shop Members</h3>
          <p className="text-muted text-sm">
            Manage member access and permissions for this shop
          </p>
        </div>
        {canManageMembers && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="btn btn-primary"
          >
            <span className="mr-2">+</span>
            Invite Member
          </button>
        )}
      </div>

      {error && (
        <div className="error-banner card mb-6">
          <div className="text-error">{error}</div>
        </div>
      )}

      {/* Members List */}
      <div className="members-list">
        {members.length === 0 ? (
          <div className="empty-state card text-center py-12">
            <div className="text-6xl mb-4">👥</div>
            <h3 className="heading-secondary text-xl mb-2">No members yet</h3>
            <p className="text-muted mb-6">
              Invite team members to collaborate on this shop
            </p>
            {canManageMembers && (
              <button
                onClick={() => setShowInviteModal(true)}
                className="btn btn-primary"
              >
                Invite Your First Member
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {members.map((member) => (
              <div
                key={member.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  backgroundColor: 'var(--bg-primary)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '6px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: getAvatarColor(member.email || member.displayName || member.firebaseUid),
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 600,
                      flexShrink: 0
                    }}
                  >
                    {getAvatarInitial(member)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)' }}>
                      {member.displayName || member.email || 'Unknown User'}
                    </div>
                    {member.email && member.email !== (member.displayName || member.email) && (
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '1px' }}>
                        {member.email}
                      </div>
                    )}
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                      <span className={`status-badge ${member.active ? 'active' : 'inactive'}`} style={{ fontSize: '11px', padding: '2px 6px' }}>
                        {member.active ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                      <span style={{ margin: '0 6px' }}>•</span>
                      Joined {formatDate(member.joinedAt)}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'flex-end' }}>
                    {member.roles.map((role) => (
                      <span
                        key={role}
                        style={{
                          padding: '2px 8px',
                          backgroundColor: 'var(--bg-accent)',
                          color: 'var(--primary-dark)',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: 500,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {role}
                      </span>
                    ))}
                  </div>

                  {canEditMember(member) && (
                    <div style={{ display: 'flex', gap: '8px', marginLeft: '12px' }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ fontSize: '12px', padding: '4px 8px' }}
                        onClick={() => setEditingMember(member)}
                      >
                        Edit Roles
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ fontSize: '12px', padding: '4px 8px', color: member.active ? 'var(--error-text)' : 'var(--success-dark)' }}
                        onClick={() => setMemberActive(member.id, !member.active)}
                      >
                        {member.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending Invitations Section */}
      {canManageMembers && pendingInvitations.length > 0 && (
        <div className="pending-invitations-section" style={{ marginTop: '48px', paddingTop: '32px', borderTop: '1px solid var(--border-light)' }}>
          <h3 className="heading-secondary text-base mb-4">
            Pending Invitations ({pendingInvitations.length})
          </h3>

          <div className="space-y-2">
            {pendingInvitations.map((invitation) => (
              <div
                key={invitation.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-card)'
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>
                    {invitation.email}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    Expires {formatDate(invitation.expiresAt)}
                    {invitation.roles.length > 0 && (
                      <>
                        <span style={{ margin: '0 6px' }}>•</span>
                        {invitation.roles.map((role, idx) => (
                          <span key={role}>
                            {idx > 0 && ', '}
                            {role}
                          </span>
                        ))}
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleRevokeInvitation(invitation.id)}
                  style={{
                    color: 'var(--error-text)',
                    border: '1px solid var(--error)',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                    fontWeight: 500
                  }}
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && (
        <InviteMemberModal
          shopId={shopId}
          onClose={() => setShowInviteModal(false)}
          onMemberInvited={handleMemberInvited}
        />
      )}

      {/* Edit Member Roles Modal */}
      {editingMember && (
        <EditMemberRolesModal
          member={editingMember}
          onClose={() => setEditingMember(null)}
          onRolesUpdated={handleRoleUpdate}
        />
      )}

      {/* Resource limit reached (CSAI-533) */}
      {rateLimitError && (
        <RateLimitModal
          error={rateLimitError}
          onClose={() => setRateLimitError(null)}
        />
      )}
    </div>
  );
};

export default ShopMembersTab;